'use client';

import React, { useEffect, useRef, useState } from 'react';
import { SiGithub, SiX } from '@icons-pack/react-simple-icons';
import AnimateOnScroll from './AnimateOnScroll';
import { gridMetrics } from './hero/HeroGrid';
import { routePath } from '@/lib/routes';
import StatusIndicator from './status/StatusIndicator';

// Simple Icons dropped the LinkedIn logo over trademark enforcement; inline fallback.
const LinkedinIcon = ({ size = 24, ...props }: { size?: number } & React.SVGProps<SVGSVGElement>) => (
  <svg role="img" viewBox="0 0 24 24" width={size} height={size} fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

/** Minimum number of cells the footer content occupies — below this the footer
 *  falls back to a regular centered block with no side grid (mirrors Navbar). */
const MIN_FOOTER_CELLS = 6;

/** Grid line pattern, painted only in the footer's side gutters so the content
 *  area is never covered by the grid. Faint white lines for the dark footer.
 *  The footer's own opaque background hides the page (body) grid, so this is the
 *  ONLY grid layer — no doubling. */
const GRID_IMAGE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(to right,  rgba(255,255,255,0.06) 1px, transparent 1px),' +
    'linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)',
  backgroundSize: 'var(--grid-cell, 72px) var(--grid-cell, 72px)',
};

/** Social profiles — flip `enabled` once the profile exists. */
const SOCIALS: {
  label: string;
  href: string;
  Icon: React.ComponentType<{ size?: number }>;
  enabled: boolean;
}[] = [
  { label: 'GitHub', href: 'https://github.com/rii-link', Icon: SiGithub, enabled: true },
  { label: 'X', href: '#', Icon: SiX, enabled: false }, // no profile yet
  { label: 'LinkedIn', href: '#', Icon: LinkedinIcon, enabled: false }, // no profile yet
];

/**
 * Full-bleed section divider that lies exactly on the footer's grid rows.
 * The footer paints its gutter grid from its own top edge, so the divider
 * snaps to `footerTop + n·cell` — it reads as one grid line running from
 * viewport edge to viewport edge, through the content area. The slot is one
 * cell tall, so exactly one grid row boundary crosses it.
 */
const GridDivider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const slotRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number | null>(null);

  useEffect(() => {
    const update = () => {
      const el = slotRef.current;
      const footer = el?.closest('footer');
      if (!el || !footer) return;
      const { cell } = gridMetrics(document.documentElement.clientWidth);
      const slotTop = el.getBoundingClientRect().top;
      const footerTop = footer.getBoundingClientRect().top;
      const offset = slotTop - footerTop; // footer-local y of the slot
      const line = Math.ceil(offset / cell) * cell; // next grid row boundary
      setTop(Math.min(Math.max(line - offset, 0), cell));
    };
    update();
    window.addEventListener('resize', update);
    // Content height changes above the slot move it — re-snap.
    const ro = new ResizeObserver(update);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, []);

  if (children) {
    // Divider line on the grid boundary + content filling EXACTLY one grid
    // row below it (vertically centered). Mobile falls back to a padded
    // stack — the grid isn't followed on phones.
    return (
      <div ref={slotRef} className="relative" style={{ paddingTop: top ?? 24 }}>
        <div
          aria-hidden
          className="absolute left-1/2 h-px w-screen -translate-x-1/2 bg-white/10"
          style={{ top: top ?? 0 }}
        />
        <div className="flex flex-col justify-center gap-4 py-5 md:h-[var(--grid-cell,72px)] md:flex-row md:items-center md:justify-between md:py-0">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={slotRef}
      aria-hidden
      className="relative"
      style={{ height: 'var(--grid-cell, 72px)' }}
    >
      <div
        className="absolute left-1/2 h-px w-screen -translate-x-1/2 bg-white/10"
        style={{ top: top ?? '50%' }}
      />
    </div>
  );
};

/** Smooth scroll-to-top; jumps instantly for reduced-motion users. */
const scrollToTop = () => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
};

type LinkColumn = { title: string; links: { label: string; href: string }[] };

const LINK_COLUMNS: LinkColumn[] = [
  {
    title: 'Product',
    links: [
      { label: 'Link Management', href: routePath('linkManagement') },
      { label: 'Smart Routing', href: routePath('smartRouting') },
      { label: 'Dynamic Responses', href: routePath('dynamicResponses') },
      { label: 'AI & MCP', href: routePath('aiMcp') },
      { label: 'Analytics', href: routePath('analytics') },
      { label: 'QR Codes', href: routePath('qrCodes') },
      { label: 'Custom Domains', href: routePath('customDomains') },
      { label: 'Integrations', href: routePath('integrations') },
      { label: 'Pricing', href: routePath('pricing') },
    ],
  },
  {
    title: 'Solutions',
    links: [
      { label: 'Marketing', href: routePath('solutionsMarketing') },
      { label: 'Enterprise', href: routePath('solutionsEnterprise') },
      { label: 'Developers', href: routePath('developers') },
      { label: 'Docs', href: routePath('docs') },
      { label: 'Changelog', href: routePath('changelog') },
      { label: 'AI apps', href: routePath('aiMcp') },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', href: routePath('about') },
      { label: 'Security', href: routePath('security') },
      { label: 'Check a link', href: routePath('check') },
      { label: 'Report abuse', href: routePath('reportAbuse') },
      { label: 'The Redirect', href: routePath('learn') },
      { label: 'Contact', href: routePath('contact') },
    ],
  },
  {
    // SEO comparison pages — dummy targets for now, pages to follow.
    title: 'Compare',
    links: [
      { label: 'rii.link vs Bitly', href: routePath('compareBitly') },
      { label: 'rii.link vs Dub', href: routePath('compareDub') },
      { label: 'rii.link vs Rebrandly', href: routePath('compareRebrandly') },
      { label: 'rii.link vs Short.io', href: routePath('compareShortIo') },
      { label: 'rii.link vs TinyURL', href: routePath('compareTinyurl') },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: routePath('legalPrivacy') },
      { label: 'Terms of Service', href: routePath('legalTerms') },
      { label: 'Imprint', href: routePath('legalImprint') },
    ],
  },
];

const Footer: React.FC = () => {
  const [contentStyle, setContentStyle] = useState<React.CSSProperties>({});
  const [leftStyle, setLeftStyle] = useState<React.CSSProperties>({});
  const [rightStyle, setRightStyle] = useState<React.CSSProperties>({});

  // Mirror the Navbar: the content chunk spans the grid container and carries a
  // solid background, so the page grid is hidden behind it. The grid is painted
  // only in the two side gutters, aligned to the same page grid as everything
  // else. Narrow viewports fall back to a plain centered block.
  useEffect(() => {
    const update = () => {
      const w = document.documentElement.clientWidth;
      const { cell, snapped, offsetX } = gridMetrics(w);
      if (snapped < (MIN_FOOTER_CELLS + 2) * cell) {
        setContentStyle({ maxWidth: '1280px', marginLeft: 'auto', marginRight: 'auto' });
        setLeftStyle({});
        setRightStyle({});
        return;
      }
      // Redraw only the LEFT edge divider; the right divider is the right strip's
      // first grid line, so drawing it here too would double it.
      setContentStyle({
        width: `${snapped}px`,
        marginLeft: `${offsetX}px`,
        boxShadow: 'inset 1px 0 0 0 rgba(255,255,255,0.07)',
      });
      setLeftStyle({ width: `${offsetX}px`, backgroundPosition: `${offsetX}px 0`, ...GRID_IMAGE });
      setRightStyle({ width: `${w - offsetX - snapped}px`, backgroundPosition: '0 0', ...GRID_IMAGE });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return (
    <AnimateOnScroll animation="fade-in">
      {/* Opaque dark background spans the full width (gutters included) so it
          covers the page grid — that's what prevents the doubled lines: the only
          grid that paints is the faint one in the side gutters below. */}
      {/* overflow-x-clip: the slot divider hairlines are full-bleed via
          `w-screen` (100vw), which includes the scrollbar width — without the
          clip they poke ~7px past the layout viewport and give every page a
          horizontal wiggle (#182/#225). `clip` (not `hidden`) so the footer
          doesn't become a scroll container. */}
      <footer data-nav-surface="dark" className="relative overflow-x-clip bg-slate-900">
        {/* Gradient top border */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent z-[2]" />

        {/* Grid painted ONLY in the side gutters — never behind the content. */}
        <div aria-hidden className="grid-gutter absolute inset-y-0 left-0 pointer-events-none" style={leftStyle} />
        <div aria-hidden className="grid-gutter absolute inset-y-0 right-0 pointer-events-none" style={rightStyle} />

        {/* Content chunk — spans the centered grid band; sits on the footer's dark
            background with no grid behind it (the grid lives in the gutters). */}
        {/* No bottom padding — the footer ends exactly on the last grid row. */}
        <div className="grid-edge relative z-[1] px-6 sm:px-8 pt-16" style={contentStyle}>
          {/* Top region: brand · link columns */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
            {/* Brand */}
            <div className="lg:col-span-4">
              <div className="flex items-center space-x-2 mb-4">
                <img
                  src="/logos/icon-on-dark.png"
                  alt=""
                  aria-hidden
                  className="w-6 h-6 rounded-md select-none"
                />
                <span className="text-lg font-bold text-white">rii.link</span>
              </div>
              <p className="text-sm text-slate-400 mb-6 max-w-xs">
                The intelligent routing layer for the modern web. Built for developers and marketers.
              </p>
              <div className="flex space-x-4">
                {SOCIALS.filter((s) => s.enabled).map(({ label, href, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    aria-label={label}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-500 hover:text-white hover:scale-110 hover:-translate-y-0.5 transition-all"
                  >
                    <Icon size={20} />
                  </a>
                ))}
              </div>
            </div>

            {/* Link columns */}
            <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 lg:ml-auto">
              {LINK_COLUMNS.map((col) => (
                <div key={col.title}>
                  <h4 className="font-bold text-white mb-4">{col.title}</h4>
                  <ul className="space-y-2 text-sm text-slate-400">
                    {col.links.map((link) => (
                      <li key={link.label}>
                        <a href={link.href} className="hover:text-brand-400 transition-colors">{link.label}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Divider on the footer grid, edge to edge. */}
          <GridDivider />

          {/* Bottom region: large wordmark (left) + trust line (right) */}
          <div>
            <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
              {/* Large brand wordmark (logo-light = white + pink, for dark
                  surfaces) — doubles as a back-to-top control. */}
              <button
                type="button"
                aria-label="Back to top"
                onClick={scrollToTop}
                className="w-fit cursor-pointer transition-opacity hover:opacity-80"
              >
                <img
                  src="/logos/logo-light.png"
                  alt="rii.link"
                  className="h-20 sm:h-24 lg:h-28 w-auto select-none"
                />
              </button>

              {/* Trust line — facts only. The old 4.8/5 rating and "2,400+ teams /
                  12M clicks" were dummy numbers (claims sweep, 2026-09-27). */}
              <div className="w-full max-w-sm lg:shrink-0">
                <p className="text-lg font-bold text-white">EU-first — visitor IPs anonymised before storage.</p>
                <p className="mt-3 text-sm text-slate-400">
                  No selling of click data, ever.{' '}
                  <a href={routePath('status')} className="font-semibold text-white hover:text-brand-400">
                    Live status ↗
                  </a>
                </p>
              </div>
            </div>

          </div>

          {/* Copyright · status — exactly one grid row below the divider. */}
          <GridDivider>
            <span className="text-sm text-slate-500">
              &copy; {new Date().getFullYear()} rii.link. All rights reserved.
            </span>
            <StatusIndicator />
          </GridDivider>
        </div>
      </footer>
    </AnimateOnScroll>
  );
};

export default Footer;
